import { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 4v10" />
    <path d="m8.5 10.5 3.5 3.5 3.5-3.5" />
    <path d="M5 18h14" />
  </svg>
);

const getPdfLabels = (language) => {
  const t = i18n.getFixedT(language === "ar" ? "ar" : "en");

  return {
    coverTitle: t("pdfExport.coverTitle"),
    coverSubtitle: t("pdfExport.coverSubtitle"),
    generatedAt: t("pdfExport.generatedAt"),
    tableOfContents: t("pdfExport.tableOfContents"),
    pageNumberLabel: t("pdfExport.pageNumberLabel"),
  };
};

const DownloadPdfButton = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const isRtl = i18nInstance.language?.startsWith("ar");
  const [isPreparing, setIsPreparing] = useState(false);

  const handleDownload = async () => {
    if (isPreparing) {
      return;
    }

    const language = i18nInstance.language?.startsWith("ar") ? "ar" : "en";
    setIsPreparing(true);

    try {
      const { downloadDocumentationPdf } = await import(
        "../utils/documentPdfExport"
      );

      await downloadDocumentationPdf({
        language,
        labels: getPdfLabels(language),
      });
    } catch (error) {
      console.error("Failed to generate documentation PDF", error);
      const detail =
        error instanceof Error && error.message
          ? `\n\n${error.message}`
          : "";
      window.alert(`${t("pdfExport.errorMessage")}${detail}`);
    } finally {
      setIsPreparing(false);
    }
  };

  const renderButtonLabel = () => {
    if (!isRtl) {
      return t("pdfExport.buttonLabel");
    }

    return (
      <span className="download-pdf-button-label-text">
        <span>{t("pdfExport.downloadAction")}</span>
        <span dir="ltr" className="download-pdf-label-suffix">
          PDF
        </span>
      </span>
    );
  };

  return (
    <button
      type="button"
      className={`download-pdf-btn${isPreparing ? " is-preparing" : ""}`}
      onClick={handleDownload}
      aria-label={t("pdfExport.buttonLabel")}
      disabled={isPreparing}
    >
      {isPreparing ? (
        <span className="download-pdf-spinner" aria-hidden="true" />
      ) : (
        <span className="download-pdf-icon">
          <DownloadIcon />
        </span>
      )}

      <span className="download-pdf-label">
        {isPreparing ? t("pdfExport.preparing") : renderButtonLabel()}
      </span>
    </button>
  );
};

export default DownloadPdfButton;
